import PollerRejection from '../enums/PollerRejection';
import getIsSessionIdValid from '../getIsSessionIdValid';
import getSessionId from '../getSessionId';
import {redirect} from '../v2/api/redirect';
import gtmPushCheckoutPageErrors from '../v2/google-tag-manager/gtmPushCheckoutPageErrors';
import {getSelectedPaymentOptionType} from '../v2/selectors/selectors';
import store from '../v2/store/store';
import bigQueryMetricRecorder from '../v2/tracking/bigQueryMetricRecorder';
import Track from '../v2/utils/tracking';

function getDefaults() {
  const defaultOptions = window.checkout.poller || {};
  return {
    pollDuration: defaultOptions.pollDuration || 1000, // polls every 'pollduration' ms
    pollTimeout: defaultOptions.pollTimeout || 10000, // each polling request timeout
    pollMaxTime: defaultOptions.pollMaxTime || 30000, // default max timeout
  };
}

function isValid() {
  return getIsSessionIdValid();
}

// Constructor func.
class Poller {
  constructor(options, resolve, reject) {
    this.options = {...getDefaults(), ...options};
    this.sessionId = getSessionId();
    this.isPolling = false;
    this.pollStartTime = 0;
    this.lastPollEndTime = 0;
    
    this.resolve = resolve;
    this.reject = reject;
    
    this.execPost = this.execPost.bind(this);
    this.runPoller = this.runPoller.bind(this);
    this.stopPoller = this.stopPoller.bind(this);
    this.getRequestOptions = this.getRequestOptions.bind(this);
    this.fetchRejectionHandler = this.fetchRejectionHandler.bind(this);
    this.hasTimeoutBeenReached = this.hasTimeoutBeenReached.bind(this);
    this.setCurrentRequestFinished = this.setCurrentRequestFinished.bind(this);
    this.poll = this.poll.bind(this);
    this.pollIfNecessary = this.pollIfNecessary.bind(this);
    this.handleResponse = this.handleResponse.bind(this);
    this.genericAPIRejection = this.genericAPIRejection.bind(this);
  }
  
  static mergeResponseProperties(response, body) {
    const {
      headers, ok, redirected, status, statusText, type, url,
    } = response;
    
    return {
      body,
      url, // Can't spread Response, so this is the next best option
      headers,
      ok,
      redirected,
      status,
      statusText,
      type,
    };
  }
  
  runPoller() {
    if (this.options.pollURL) {
      if (!isValid()) {
        redirect.toRedirectPage();
        return;
      }
      
      this.pollStartTime = new Date().getTime();
      
      if (!this.isPolling) {
        this.isPolling = true;
        
        this.poll();
        
        this.pollerTimer = setInterval(this.pollIfNecessary, this.options.pollDuration);
      }
    }
  }
  
  pollIfNecessary() {
    if (!this.requestInFlight && !this.hasTimeoutBeenReached()) {
      this.poll();
    }
  }
  
  hasTimeoutBeenReached() {
    return this.options.pollMaxTime !== null
      && this.lastPollEndTime - this.pollStartTime >= this.options.pollMaxTime;
  }
  
  execPost(body = {}, method = 'POST', url = this.options.postURL, contentType = 'application/x-www-form-urlencoded; charset=UTF-8') {
    if (!isValid()) {
      redirect.toRedirectPage();
      return Promise.resolve();
    }
    
    const responseHandlers = {
      200(response) {
        this.resolve(response);
        
        // Stop polling.
        this.stopPoller();
      },
      204(response) {
        this.genericAPIRejection(response);
        this.stopPoller();
      },
      202() {
        this.runPoller();
      },
      410() {
        window.location = '/checkout/timeout';
      },
      422(data) {
        gtmPushCheckoutPageErrors([{
          type: data.body?.message || `422 status received when execPost to ${url}`,
          label: '',
        }]);
        this.genericAPIRejection(data);
      },
      404(data) {
        gtmPushCheckoutPageErrors([{
          type: data.body?.message || `404 status received when execPost to ${url}`,
          label: '',
        }]);
        this.genericAPIRejection(data);
      },
      400(data) {
        gtmPushCheckoutPageErrors([{
          type: data.body?.message || `400 status received when execPost to ${url}`,
          label: '',
        }]);
        this.genericAPIRejection(data);
      },
      500(data) {
        gtmPushCheckoutPageErrors([{
          type: data.body?.message || `500 status received when execPost to ${url}`,
          label: '',
        }]);
        this.genericAPIRejection(data);
      },
    };
  
    this.body = body;
    this.postStart = Date.now();
    
    return fetch(url, this.getRequestOptions(body, method, contentType))
      .then(this.handleResponse(responseHandlers), this.fetchRejectionHandler)
      .catch(Track.track);
  }
  
  stopPoller() {
    this.pollStartTime = 0;
    this.lastPollEndTime = 0;
    this.isPolling = false;
  
    if (this.postStart) {
      const totalPollingTime = Date.now() - this.postStart;
      this.postStart = 0;
      try {
        bigQueryMetricRecorder(
          'pollDuration',
          [
            {name: 'duration', value: totalPollingTime},
            {name: 'poller', value: this.options.postURL},
            {name: 'selectedPaymentMethod', value: getSelectedPaymentOptionType(store.getState())},
          ],
        );
      } catch (error) {
        Track.track(error);
      }
    }
    
    if (this.pollerTimer) {
      clearInterval(this.pollerTimer);
    }
  }
  
  getRequestOptions(body, method = 'GET', contentType = 'application/json') {
    return {
      method,
      body,
      mode: 'cors',
      cache: 'no-cache',
      credentials: 'same-origin', // To automatically include cookies with the request
      headers: {
        'Content-Type': contentType,
        sessionId: this.sessionId,
        'x-thg-client': 'checkout-web-js',
      },
    };
  }
  
  fetchRejectionHandler(error) {
    this.setCurrentRequestFinished();
    Track.track(error);
    this.reject({reason: PollerRejection.NETWORK});
    this.stopPoller();
  }
  
  poll() {
    const responseHandlers = {
      202() {
        // Poll returned, but hasn't succeeded yet.
        if (this.hasTimeoutBeenReached()) {
          // Final poll and nothing has succeeded.
          this.reject({reason: PollerRejection.TIMEOUT});
          this.stopPoller();
        }
      },
      200(data) {
        // Polling succeeded.
        this.resolve(data);
        this.stopPoller();
      },
      410() {
        this.lastPollEndTime = new Date().getTime();
        window.location = '/checkout/timeout';
      },
      403() {
        this.reject({reason: PollerRejection.API, status: 403});
        this.stopPoller();
      },
      422(data) {
        gtmPushCheckoutPageErrors([{type: data.body?.message || '422 status received when polling', label: ''}]);
        this.genericAPIRejection(data);
      },
      404(data) {
        gtmPushCheckoutPageErrors([{type: data.body?.message || '404 status received when polling', label: ''}]);
        this.genericAPIRejection(data);
      },
      400(data) {
        gtmPushCheckoutPageErrors([{type: data.body?.message || '400 status received when polling', label: ''}]);
        this.genericAPIRejection(data);
      },
      500(data) {
        gtmPushCheckoutPageErrors([{type: data.body?.message || '500 status received when polling', label: ''}]);
        this.genericAPIRejection(data);
      },
    };
    
    this.requestInFlight = true;
  
    const body = this.options.sendBodyOnPoll ? this.body : undefined;
    const method = this.options.sendBodyOnPoll ? 'POST' : undefined;
  
    return fetch(this.options.pollURL, this.getRequestOptions(body, method))
      .then(this.handleResponse(responseHandlers), this.fetchRejectionHandler)
      .catch(Track.track);
  }
  
  genericAPIRejection(response) {
    this.reject({...response, reason: PollerRejection.API});
    this.stopPoller();
  }
  
  setCurrentRequestFinished() {
    this.lastPollEndTime = new Date().getTime();
    this.requestInFlight = false;
  }
  
  handleResponse(responseHandlers) {
    return (response) => {
      this.setCurrentRequestFinished();
      const handleResponse = responseHandlers[response.status] || this.genericAPIRejection;
      
      return response.json()
        .catch(() => ({})) // If can't process JSON, use empty object instead.
        .then(body => Poller.mergeResponseProperties(response, body))
        .then(handleResponse.bind(this));
    };
  }
}

export default Poller;
